import { AxiosRequestConfig, IApiError } from '@frontend/request';

import { IAPIControllerCallbacks } from '@aqx-core/common/api/types';
import {
  IGetTechnicalIndicatorParams,
  IGetUserPreferenceParams,
  IOrderColumnPreference,
  IOrderColumnPreferenceV2Map,
  ITechnicalIndicator,
  IUpdateLanguage,
  IUpdateOrderColumnPreferenceParams,
  IUpdateOrderColumnPreferenceParamsV2,
  IUpdateTechnicalIndicatorParams,
  IUpdateUserPreferenceParams,
  IUserPreference,
  userAccountSettingAPI
} from '@aqx-core/common/api/user-account-setting';

class UserAccountSettingAPIController {
  private orderColumnPreferencesAC?: AbortController;

  static GET_TECHNICAL_INDICATOR_RETRY = 3;

  public getTechnicalIndicator = async (
    params: IGetTechnicalIndicatorParams | undefined,
    callbacks: IAPIControllerCallbacks<ITechnicalIndicator[]>
  ) => {
    try {
      const { result } = await userAccountSettingAPI.getTechnicalIndicator(
        params,
        UserAccountSettingAPIController.GET_TECHNICAL_INDICATOR_RETRY
      );
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public updateTechnicalIndicator = async (
    params: IUpdateTechnicalIndicatorParams,
    callbacks: IAPIControllerCallbacks<boolean>,
    signal?: AbortSignal
  ) => {
    try {
      const { result } = await userAccountSettingAPI.updateTechnicalIndicator(params, signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public updateLanguage = async (callbacks: IAPIControllerCallbacks<IUpdateLanguage>) => {
    try {
      const { result } = await userAccountSettingAPI.updateLanguage();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public qrCode = async (callbacks: IAPIControllerCallbacks<string>) => {
    try {
      const { result } = await userAccountSettingAPI.qrCode();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getOrderColumnPreferences = async (callbacks: IAPIControllerCallbacks<IOrderColumnPreference[]>) => {
    try {
      this.orderColumnPreferencesAC?.abort();
      this.orderColumnPreferencesAC = new AbortController();

      const { result } = await userAccountSettingAPI.getOrderColumnPreferences(this.orderColumnPreferencesAC.signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public updateOrderColumnPreference = async (
    params: IUpdateOrderColumnPreferenceParams,
    callbacks: IAPIControllerCallbacks<boolean>
  ) => {
    try {
      const { result } = await userAccountSettingAPI.updateOrderColumnPreference(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public updateOrderColumnPreferenceV2 = async (
    params: IUpdateOrderColumnPreferenceParamsV2,
    callbacks: IAPIControllerCallbacks<boolean>
  ) => {
    try {
      const { result } = await userAccountSettingAPI.updateOrderColumnPreferenceV2(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getUserPreference = async (
    params: IGetUserPreferenceParams | undefined,
    callbacks: IAPIControllerCallbacks<IUserPreference[]>
  ) => {
    try {
      const { result } = await userAccountSettingAPI.getUserPreference(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public updateUserPreference = async (
    params: IUpdateUserPreferenceParams,
    callbacks: IAPIControllerCallbacks<boolean>,
    signal?: AbortSignal
  ) => {
    try {
      const { result } = await userAccountSettingAPI.updateUserPreference(params, signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getAllColumnPreferences = async (
    callbacks: IAPIControllerCallbacks<IOrderColumnPreference[]>,
    config?: AxiosRequestConfig
  ) => {
    try {
      const { result } = await userAccountSettingAPI.getAllColumnPreferences(config);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getAllColumnPreferencesV2 = async (
    callbacks: IAPIControllerCallbacks<IOrderColumnPreferenceV2Map>,
    config?: AxiosRequestConfig
  ) => {
    try {
      const { result } = await userAccountSettingAPI.getAllColumnPreferencesV2(config);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public hideColumnPreferences = async (
    params: IUpdateOrderColumnPreferenceParams,
    callbacks: IAPIControllerCallbacks<boolean>,
    config?: AxiosRequestConfig
  ) => {
    try {
      const { result } = await userAccountSettingAPI.hideColumnPreferences(params, config);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };
}

export const userAccountSettingAPIController = new UserAccountSettingAPIController();
